import { AxiosInstance } from 'axios';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';
import { BaseApiService } from './base-api.service';

export interface SubscriptionPlanTier {
  id: string;
  title?: string;
  subTitle?: string;
  maxTrees: number;
  price: number;
  benefits: string[];
  benefitsHtml?: string;
  maxAlignments: number;  // -1 = illimité, 0 = interdit (BASIC)
  maxEpaysages: number;   // -1 = illimité, 0 = interdit (BASIC)
  /** ID du Product Stripe associé à ce plan */
  stripeProductId?: string;
  /** ID du Price Stripe actif pour ce tier — source de vérité pour le checkout */
  stripePriceId?: string;
}

export interface SubscriptionPlanConfig {
  tiers: SubscriptionPlanTier[];
  maxAlignments: number; // -1 means unlimited
  maxEpaysages: number;  // -1 means unlimited
}

export interface SubscriptionConfig {
  plans: {
    BASIC: SubscriptionPlanConfig;
    ADVANCED: SubscriptionPlanConfig;
    [key: string]: SubscriptionPlanConfig;
  };
  requirePaymentBeforeSignup?: boolean;
  allowFreeTrial?: boolean;
  freeTrialDurationDays?: number;
  freeTrialMaxTrees?: number;
  freeTrialMaxFiches?: number;
}

export class SettingsService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.SETTINGS.BASE, axios);
  }
  /**
   * Récupère la configuration des abonnements
   */
  async getSubscriptionConfig(): Promise<SubscriptionConfig> {
    const { data } = await this.axios.get(`${ENDPOINTS.SETTINGS.SUBSCRIPTION}`);
    return data.data;
  }

  /**
   * Récupère la configuration publique (sans auth) pour l'affichage marketing
   */
  async getPublicSubscriptionConfig(): Promise<SubscriptionConfig> {
    const { data } = await this.axios.get(`${ENDPOINTS.SETTINGS.SUBSCRIPTION_PUBLIC}`);
    return data.data;
  }

  /**
   * Met à jour la configuration des abonnements
   */
  async updateSubscriptionConfig(config: SubscriptionConfig): Promise<SubscriptionConfig> {
    const { data } = await this.axios.put(`${ENDPOINTS.SETTINGS.SUBSCRIPTION}`, config);
    return data.data;
  }

  /**
   * Initialise les paramètres par défaut
   */
  async initializeDefaultSettings(): Promise<SubscriptionConfig> {
    const { data } = await this.axios.put(`${ENDPOINTS.SETTINGS.SUBSCRIPTION_INITIALIZE}`);
    return data.data;
  }

  /**
   * Déclenche la synchronisation Stripe (produits + prices).
   * Stocke stripePriceId/stripeProductId dans les tiers et archive les anciens prices.
   * Requiert le rôle ADMIN.
   */
  async syncStripe(): Promise<{
    created: number;
    updated: number;
    archived: number;
    errors: Array<{ planType: string; tierId: string; error: string }>;
  }> {
    const { data } = await this.axios.post(`${ENDPOINTS.SETTINGS.SUBSCRIPTION}/stripe-sync`);
    return data;
  }
}

export const settingsService = new SettingsService();
